import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles, Save, Send, Play, Check, ShieldCheck, Mic, Bot, BookOpen, Workflow, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VoiceLayout } from './VoiceLayout';
import { usePageTitle } from '@/lib/usePageTitle';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const STEPS = [
  { id: 1, label: 'Basic',        icon: FileText },
  { id: 2, label: 'Voice',        icon: Mic },
  { id: 3, label: 'Personality',  icon: Bot },
  { id: 4, label: 'Objectives',   icon: Check },
  { id: 5, label: 'Scripts',      icon: FileText },
  { id: 6, label: 'Knowledge',    icon: BookOpen },
  { id: 7, label: 'Guardrails',   icon: ShieldCheck },
  { id: 8, label: 'Workflow',     icon: Workflow },
  { id: 9, label: 'Review',       icon: Check },
];

const DEFAULT_PERSONALITY = 'You are a professional Saudi Arabic collections agent. You speak naturally over the phone using respectful Saudi dialect. You are calm, empathetic, and clear. You verify identity before discussing any debt details. You help the debtor understand available options without threats, pressure, or inappropriate language.';

const AR_OPENING = 'السلام عليكم، معك من فريق المتابعة بخصوص حسابك. عندك دقيقة؟';

export default function CreateAgent() {
  usePageTitle('Create AI Voice Agent');
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '', type: 'Early Reminder', desc: '', owner: 'Noura Al-Dosari',
    language: 'Arabic', dialect: 'Saudi Najdi', status: 'Draft',
    voiceProvider: 'Mock ElevenLabs', gender: 'Female', age: 'Adult',
    speed: 'Normal', tone: 'Empathetic', emotion: 60, interrupt: 'Limited',
    silence: 4, maxDuration: 6, fillers: true, naturalSaudi: true,
    personality: DEFAULT_PERSONALITY,
    objectives: ['Verify debtor identity', 'Explain outstanding balance', 'Capture promise to pay', 'Offer installment plan'],
    scriptStyle: 'Natural Saudi Phone',
    opening: AR_OPENING,
    knowledge: ['Payment Plan Policy', 'SAMA Collection Guidelines', 'Hardship Program Rules'],
  });
  const [voicePreview, setVoicePreview] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const next = () => setStep((s) => Math.min(9, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  return (
    <VoiceLayout
      title="Create / configure AI voice agent"
      subtitle="Nine-step setup covering voice, personality, scripts, knowledge sources, compliance guardrails and call-flow workflow."
      actions={(<>
        <Button variant="outline" size="sm" onClick={() => toast.success('Draft saved')}><Save className="h-4 w-4 mr-1.5" aria-hidden="true" />Save draft</Button>
        <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => { toast.success('Submitted for approval'); navigate('/app/voice-agents/library'); }}><Send className="h-4 w-4 mr-1.5" aria-hidden="true" />Submit for approval</Button>
      </>)}
    >
      {/* Stepper */}
      <ol className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
        {STEPS.map((s) => (
          <li key={s.id} className="flex items-center min-w-max">
            <button onClick={() => setStep(s.id)} className={cn('px-2.5 py-2 rounded-md flex items-center gap-1.5 text-[11.5px] border',
              step === s.id ? 'border-primary bg-primary/10 text-primary font-medium' :
              step > s.id  ? 'border-success/40 bg-success/10 text-success' :
              'border-border text-muted-foreground'
            )}>
              <s.icon className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{s.id}. {s.label}</span>
            </button>
            {s.id < 9 && <ChevronRight className="h-3 w-3 text-muted-foreground mx-0.5" aria-hidden="true" />}
          </li>
        ))}
      </ol>

      <div className="surface-card p-5 min-h-[480px]">
        {step === 1 && <StepBasic form={form} update={update} />}
        {step === 2 && <StepVoice form={form} update={update} onPreview={() => setVoicePreview(true)} />}
        {step === 3 && <StepPersonality form={form} update={update} />}
        {step === 4 && <StepObjectives form={form} update={update} />}
        {step === 5 && <StepScripts form={form} update={update} />}
        {step === 6 && <StepKnowledge form={form} update={update} />}
        {step === 7 && <StepGuardrails />}
        {step === 8 && <StepWorkflow />}
        {step === 9 && <StepReview form={form} />}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={prev} disabled={step === 1}><ChevronLeft className="h-4 w-4 mr-1" aria-hidden="true" />Back</Button>
        <div className="text-[11px] text-muted-foreground">Step {step} of 9</div>
        {step < 9 ? (
          <Button onClick={next} className="bg-primary text-primary-foreground hover:bg-primary/90">Next<ChevronRight className="h-4 w-4 ml-1" aria-hidden="true" /></Button>
        ) : (
          <Button onClick={() => { toast.success(`${form.name || 'New agent'} published`); navigate('/app/voice-agents/library'); }} className="bg-success text-success-foreground hover:bg-success/90"><Check className="h-4 w-4 mr-1" aria-hidden="true" />Publish agent</Button>
        )}
      </div>

      <Dialog open={voicePreview} onOpenChange={setVoicePreview}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Voice preview</DialogTitle>
            <DialogDescription className="sr-only">
              Simulated voice preview of the selected provider and tone for this prototype.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm">Voice preview simulated for prototype.</p>
            <p className="text-[12px] text-muted-foreground">Selected: <span className="text-foreground">{form.voiceProvider} · {form.gender} · {form.age} · {form.tone}</span></p>
            <div className="rounded-md border border-border bg-muted/40 p-3 text-[12px]" dir="rtl">
              {AR_OPENING}
            </div>
            <Button onClick={() => setVoicePreview(false)} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </VoiceLayout>
  );
}

const Field = ({ label, children, hint }) => (
  <label className="block space-y-1">
    <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</span>
    {children}
    {hint && <span className="block text-[10px] text-muted-foreground/80">{hint}</span>}
  </label>
);

const StepBasic = ({ form, update }) => (
  <div className="grid md:grid-cols-2 gap-4">
    <Field label="Agent name"><Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="e.g. Sara — Early Reminder" /></Field>
    <Field label="Agent type">
      <select className="select-base" value={form.type} onChange={(e) => update('type', e.target.value)}>
        {['Early Reminder', 'Payment Arrangement', 'Hardship Intake', 'Dispute Intake', 'Payment Confirmation', 'Legal Pre-Escalation', 'Customer Support Callback'].map((o) => <option key={o}>{o}</option>)}
      </select>
    </Field>
    <Field label="Description"><Input value={form.desc} onChange={(e) => update('desc', e.target.value)} placeholder="One-sentence description" /></Field>
    <Field label="Owner"><Input value={form.owner} onChange={(e) => update('owner', e.target.value)} /></Field>
    <Field label="Default language">
      <select className="select-base" value={form.language} onChange={(e) => update('language', e.target.value)}>
        {['Arabic', 'English', 'Bilingual'].map((o) => <option key={o}>{o}</option>)}
      </select>
    </Field>
    <Field label="Dialect">
      <select className="select-base" value={form.dialect} onChange={(e) => update('dialect', e.target.value)}>
        {['Saudi Najdi', 'Saudi Hijazi', 'Gulf Arabic', 'Modern Standard Arabic', 'English'].map((o) => <option key={o}>{o}</option>)}
      </select>
    </Field>
    <Field label="Status">
      <select className="select-base" value={form.status} onChange={(e) => update('status', e.target.value)}>
        {['Draft', 'Pending Approval', 'Active'].map((o) => <option key={o}>{o}</option>)}
      </select>
    </Field>
  </div>
);

const StepVoice = ({ form, update, onPreview }) => (
  <div className="grid md:grid-cols-2 gap-4">
    <Field label="Voice provider">
      <select className="select-base" value={form.voiceProvider} onChange={(e) => update('voiceProvider', e.target.value)}>
        {['Mock ElevenLabs', 'Mock Azure Speech', 'Mock Google TTS', 'Custom'].map((o) => <option key={o}>{o}</option>)}
      </select>
    </Field>
    <Field label="Voice gender">
      <select className="select-base" value={form.gender} onChange={(e) => update('gender', e.target.value)}><option>Female</option><option>Male</option></select>
    </Field>
    <Field label="Voice age">
      <select className="select-base" value={form.age} onChange={(e) => update('age', e.target.value)}>{['Young Adult', 'Adult', 'Mature'].map((o) => <option key={o}>{o}</option>)}</select>
    </Field>
    <Field label="Speaking speed">
      <select className="select-base" value={form.speed} onChange={(e) => update('speed', e.target.value)}>{['Slow', 'Normal', 'Fast'].map((o) => <option key={o}>{o}</option>)}</select>
    </Field>
    <Field label="Tone">
      <select className="select-base" value={form.tone} onChange={(e) => update('tone', e.target.value)}>{['Professional', 'Empathetic', 'Firm', 'Calm', 'Formal'].map((o) => <option key={o}>{o}</option>)}</select>
    </Field>
    <Field label={`Emotion level — ${form.emotion}`}>
      <Slider value={[form.emotion]} onValueChange={(v) => update('emotion', v[0])} max={100} step={1} />
    </Field>
    <Field label="Interrupt handling">
      <select className="select-base" value={form.interrupt} onChange={(e) => update('interrupt', e.target.value)}>{['Allow', 'Limited', 'Do Not Interrupt'].map((o) => <option key={o}>{o}</option>)}</select>
    </Field>
    <Field label="Silence timeout (s)"><Input type="number" value={form.silence} onChange={(e) => update('silence', +e.target.value)} /></Field>
    <Field label="Max call duration (min)"><Input type="number" value={form.maxDuration} onChange={(e) => update('maxDuration', +e.target.value)} /></Field>
    <div className="flex items-center justify-between p-3 rounded-md border border-border bg-muted/30">
      <span className="text-[12px]">Filler words (e.g. &quot;uhm&quot;, &quot;تمام&quot;)</span>
      <Switch checked={form.fillers} onCheckedChange={(v) => update('fillers', v)} aria-label="Filler words" />
    </div>
    <div className="flex items-center justify-between p-3 rounded-md border border-border bg-muted/30">
      <span className="text-[12px]">Saudi natural phone style</span>
      <Switch checked={form.naturalSaudi} onCheckedChange={(v) => update('naturalSaudi', v)} aria-label="Saudi phone style" />
    </div>
    <div className="md:col-span-2">
      <Button variant="outline" onClick={onPreview} data-testid="voice-preview-btn"><Play className="h-4 w-4 mr-1.5" aria-hidden="true" />Preview voice</Button>
    </div>
  </div>
);

const StepPersonality = ({ form, update }) => (
  <Field label="Personality prompt (used to seed every conversation)">
    <Textarea rows={10} value={form.personality} onChange={(e) => update('personality', e.target.value)} />
  </Field>
);

const OBJECTIVES = ['Verify debtor identity', 'Explain outstanding balance', 'Capture promise to pay', 'Offer installment plan', 'Capture hardship case', 'Capture dispute', 'Confirm preferred payment method', 'Confirm payment date', 'Escalate to human agent', 'Update debtor contact preference', 'Stop contact if requested'];
const StepObjectives = ({ form, update }) => (
  <div className="grid md:grid-cols-2 gap-2">
    {OBJECTIVES.map((o) => {
      const on = form.objectives.includes(o);
      return (
        <label key={o} className={cn('flex items-center gap-2 p-2.5 rounded-md border cursor-pointer text-[12.5px]',
          on ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/40')}>
          <input type="checkbox" checked={on} onChange={() => update('objectives', on ? form.objectives.filter((x) => x !== o) : [...form.objectives, o])} />
          {o}
        </label>
      );
    })}
  </div>
);

const SCRIPT_SECTIONS = [
  { id: 'opening',  label: 'Opening',                  ar: 'السلام عليكم، معك من فريق المتابعة بخصوص حسابك. عندك دقيقة؟' },
  { id: 'verify',   label: 'Identity verification',    ar: 'عشان نحافظ على خصوصيتك، أحتاج أتأكد من هويتك قبل ما أشارك أي تفاصيل. ممكن تأكد لي آخر أربع أرقام من رقم الحساب وتاريخ الميلاد؟' },
  { id: 'purpose',  label: 'Purpose of call',           ar: 'تواصلنا معك لأن ظاهر عندنا مبلغ مستحق ومتأخر قدره {{account_balance}}، وهدفنا نشوف معك حل مناسب لترتيب السداد بطريقة تناسب وضعك.' },
  { id: 'options',  label: 'Payment options',           ar: 'تقدر تسدد المبلغ كامل، أو نرتب لك خطة سداد على دفعات حسب المتاح، وإذا تمر بظرف صعب ممكن نراجع أهلية برنامج التيسير أو إعادة الجدولة.' },
  { id: 'hardship', label: 'Hardship handling',         ar: 'أفهم عليك، وأقدّر إنك وضحت لي ظروفك. عندنا بعض الخيارات اللي ممكن تساعدك، تسمح لي أشرحها لك؟' },
  { id: 'dispute',  label: 'Dispute handling',          ar: 'أفهم إن عندك اعتراض على المبلغ. راح أسجل أن المبلغ محل اعتراض، ونوضح لك خطوات تقديم الاعتراض والمستندات المطلوبة عشان تتم مراجعته من الفريق المختص.' },
  { id: 'commit',   label: 'Commitment confirmation',   ar: 'شاكرين لك تعاونك معنا. خلني أتأكد من الاتفاق: المبلغ المتفق عليه هو {{committed_amount}}، وموعد السداد {{payment_date}}، وطريقة السداد المفضلة {{payment_method_preference}}. هل هذا صحيح؟' },
  { id: 'nocontact',label: 'No-contact request',        ar: 'تم، راح نسجل طلبك بعدم التواصل، وما راح نستمر في المكالمة. شكراً لك.' },
  { id: 'closing',  label: 'Closing',                   ar: 'شكراً لتعاونك ووقتك. نتمنى لك يوم طيب.' },
];
const StepScripts = ({ form, update }) => {
  const [sections, setSections] = useState(SCRIPT_SECTIONS);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5 flex-wrap">
        {['Formal Saudi', 'Natural Saudi Phone', 'Modern Standard Arabic', 'Bilingual Arabic/English'].map((o) => (
          <button key={o} onClick={() => update('scriptStyle', o)} className={cn('px-2.5 py-1.5 text-[11px] rounded-md border',
            form.scriptStyle === o ? 'bg-primary/10 text-foreground border-primary/40' : 'border-border text-muted-foreground')}>{o}</button>
        ))}
      </div>
      {sections.map((s, i) => (
        <div key={s.id}>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{s.label}</p>
          <Textarea value={s.ar} dir="rtl" rows={2}
            onChange={(e) => setSections((arr) => arr.map((x, idx) => idx === i ? { ...x, ar: e.target.value } : x))} />
        </div>
      ))}
    </div>
  );
};

const KB_SOURCES = [
  { name: 'Payment Plan Policy',          status: 'Connected' },
  { name: 'Hardship Program Rules',       status: 'Connected' },
  { name: 'SAMA Collection Guidelines',   status: 'Connected' },
  { name: 'Objection / Dispute Process',  status: 'Pending Review' },
  { name: 'Product Terms',                status: 'Connected' },
  { name: 'Client-Specific Rules',        status: 'Outdated' },
  { name: 'Frequently Asked Questions',   status: 'Missing' },
];
const StepKnowledge = () => (
  <div className="grid md:grid-cols-2 gap-3">
    {KB_SOURCES.map((s) => (
      <div key={s.name} className="rounded-md border border-border bg-muted/30 p-3 flex items-center justify-between">
        <div>
          <p className="text-[13px] font-medium">{s.name}</p>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Knowledge base</p>
        </div>
        <Badge variant="outline" className={cn('text-[10px]',
          s.status === 'Connected'      && 'border-success/40 bg-success/10 text-success',
          s.status === 'Pending Review' && 'border-warning/40 bg-warning/10 text-warning',
          s.status === 'Outdated'       && 'border-warning/40 bg-warning/10 text-warning',
          s.status === 'Missing'        && 'border-destructive/40 bg-destructive/10 text-destructive',
        )}>{s.status}</Badge>
      </div>
    ))}
  </div>
);

const GUARDRAILS = [
  'Do not discuss debt before identity verification',
  'Do not threaten the debtor',
  'Do not mention legal action unless case is approved for escalation',
  'Do not ask for card number, IBAN, or sensitive payment details',
  'Stop the call if customer requests no further contact',
  'Escalate disputes to Legal Officer',
  'Escalate hardship cases to Supervisor Review',
  'Block calls outside allowed contact hours',
  'Block calls when frequency limit is reached',
  'Use approved scripts only',
  'Log all call outcomes',
];
const StepGuardrails = () => (
  <div className="space-y-2">
    {GUARDRAILS.map((g, i) => (
      <div key={i} className="flex items-center justify-between p-2.5 rounded-md border border-border bg-muted/30">
        <span className="text-[12.5px]">{g}</span>
        <Switch defaultChecked aria-label={g} />
      </div>
    ))}
  </div>
);

const WORKFLOW_NODES = [
  { id: 'start', label: 'Start call' },
  { id: 'verify', label: 'Verify identity' },
  { id: 'present', label: 'Present balance' },
  { id: 'options', label: 'Offer options' },
  { id: 'ptp', label: 'Capture promise to pay' },
  { id: 'plan', label: 'Create payment plan' },
  { id: 'hard', label: 'Capture hardship' },
  { id: 'disp', label: 'Capture dispute' },
  { id: 'esc', label: 'Escalate to human' },
  { id: 'follow', label: 'Send WhatsApp follow-up' },
  { id: 'link', label: 'Send payment link' },
  { id: 'end', label: 'End call' },
];
const StepWorkflow = () => (
  <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6">
    <p className="text-[11px] uppercase tracking-widest text-primary mb-3">Workflow canvas (visual preview)</p>
    <div className="grid grid-cols-3 lg:grid-cols-4 gap-3">
      {WORKFLOW_NODES.map((n, i) => (
        <div key={n.id} className={cn('rounded-md p-3 border text-[12px] relative',
          i === 0 ? 'border-success/40 bg-success/10 text-success' :
          n.id === 'end' ? 'border-destructive/40 bg-destructive/10 text-destructive' :
          n.id === 'esc' ? 'border-warning/40 bg-warning/10 text-warning' :
          'border-border bg-card')}>
          <p className="font-medium">{n.label}</p>
          {i < WORKFLOW_NODES.length - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground/40 absolute -right-3 top-1/2 -translate-y-1/2" aria-hidden="true" />}
        </div>
      ))}
    </div>
    <p className="text-[11px] text-muted-foreground mt-3">Drag-and-drop edit available in the standalone Conversation Designer.</p>
  </div>
);

const StepReview = ({ form }) => (
  <div className="grid md:grid-cols-2 gap-4 text-[13px]">
    <ReviewBlock title="Identity">
      <Row label="Name" value={form.name || '—'} />
      <Row label="Type" value={form.type} />
      <Row label="Owner" value={form.owner} />
      <Row label="Status" value={form.status} />
    </ReviewBlock>
    <ReviewBlock title="Voice & language">
      <Row label="Provider" value={form.voiceProvider} />
      <Row label="Voice" value={`${form.gender} · ${form.age} · ${form.tone}`} />
      <Row label="Language" value={`${form.language} · ${form.dialect}`} />
      <Row label="Speed" value={`${form.speed} · emotion ${form.emotion}`} />
    </ReviewBlock>
    <ReviewBlock title="Objectives">
      <ul className="text-[12px] list-disc list-inside space-y-0.5">{form.objectives.map((o) => <li key={o}>{o}</li>)}</ul>
    </ReviewBlock>
    <ReviewBlock title="Knowledge & guardrails">
      <Row label="Knowledge sources" value={form.knowledge.join(' · ')} />
      <Row label="Guardrails" value={`${GUARDRAILS.length} active`} />
      <Row label="Compliance" value="SAMA + PDPL aligned" />
    </ReviewBlock>
  </div>
);

const ReviewBlock = ({ title, children }) => (
  <div className="surface-card p-4">
    <p className="text-[10px] uppercase tracking-widest text-primary mb-2">{title}</p>
    <div className="space-y-1">{children}</div>
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between gap-2 text-[12px]"><span className="text-muted-foreground">{label}</span><span className="text-right">{value}</span></div>
);
